import React from 'react'

import { ROLES } from 'constants/roles'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { useUpdateMemberRole, useInvitableRoles, useWorkOSAuth } from 'hooks'
import { Controller, useForm } from 'react-hook-form'

import { Stack, Typography } from '@mui/material'

import { Button, Modal, TextField } from '@repo/ui'
import { Select, type SelectItem } from 'components'

interface EditCompanyUserModalProps {
  user?: {
    id: string
    email: string
    workos_membership_id: string
    role?: {
      id: string
      name: string
      slug: string
    }
  }
  open: boolean
  onClose: () => void
  onUpdated?: () => void
}

/**
 * The hook-bearing body. Split out from `EditCompanyUserModal` so the `!user`
 * guard can live in a component that calls NO hooks.
 *
 * Why not just hoist the guard below the hooks: `UsersPage` renders this modal
 * UNCONDITIONALLY with `user={selectedUser}`, which is null until a row is picked.
 * Moving `if (!user) return null` below the hooks would make `useInvitableRoles()`
 * fire a roles query on every render of the Users page. Splitting keeps the
 * original "no user, no hooks, no fetch" behaviour exactly, and satisfies
 * react-hooks/rules-of-hooks — which flagged all six hooks here as conditional.
 */
const EditCompanyUserModalContent = ({
  user,
  open,
  onClose,
  onUpdated,
}: EditCompanyUserModalProps & { user: NonNullable<EditCompanyUserModalProps['user']> }) => {
  const methods = useForm({
    defaultValues: {
      role: user?.role?.name || '',
    },
  })
  const { control, handleSubmit } = methods

  const { open: notification } = useSnackbar()
  const { user: workosUser } = useWorkOSAuth()

  // Get company ID from WorkOS user
  const companyId = workosUser?.companyId || workosUser?.company?.id || ''

  const { mutate: updateMemberRole, isPending } = useUpdateMemberRole({
    companyId,
    userId: user.id,
  })

  const { data: invitableRolesData, isLoading: isRolesLoading } = useInvitableRoles()
  // useInvitableRoles already unwraps to Role[] (hook queryFn returns response.data) — do NOT read `.data` again
  const roles = invitableRolesData || []

  const onSubmit = handleSubmit(data => {
    if (!data.role) {
      notification({
        type: 'error',
        title: 'Please select a role',
      })
      return
    }

    // Find the selected role to get its slug
    const selectedRole = roles.find(role => role.name === data.role)
    if (!selectedRole) {
      notification({
        type: 'error',
        title: 'Invalid role selected',
      })
      return
    }

    updateMemberRole(
      { roleName: selectedRole.name },
      {
        onSuccess: () => {
          notification({
            type: 'success',
            title: 'User role has been updated.',
          })
          onUpdated?.()
          internalOnClose()
        },
        onError: error => {
          const message = getErrorMessage(error)
          notification({
            type: 'error',
            title: message,
          })
        },
      }
    )
  })

  const internalOnClose = () => {
    onClose()
    methods.reset({
      role: user?.role?.name || '',
    })
  }

  // TODO(VERA-816 / VERA-786 / TD-052): consolidate Role + Department here into the
  // shared creatable <Combobox> (from agent/VERA-816). Role = select-existing (this
  // list); "add new role" routes to CreateRoleModal — deferred to the roles &
  // permissions epic (roles carry permissions; no bare-name inline create).
  const roleOptions: SelectItem[] = React.useMemo(
    () =>
      roles.map(role => ({
        label: ROLES[role.name] ?? role.name,
        value: role.name,
      })),
    [roles]
  )

  return (
    <Modal
      title="Edit user"
      open={open}
      onClose={internalOnClose}
      actions={
        <>
          <Button variant="secondary" onClick={internalOnClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={isPending} form="edit-company-user-form">
            Save Changes
          </Button>
        </>
      }
    >
      <Stack component="form" id="edit-company-user-form" onSubmit={onSubmit} spacing={4} mt={2}>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ fontSize: 16 }}>
            Email
          </Typography>
          <TextField value={user?.email || ''} disabled />
        </Stack>

        <Stack spacing={2}>
          <Typography variant="body2" sx={{ fontSize: 16 }}>
            Role
          </Typography>
          <Controller
            name="role"
            control={control}
            rules={{ required: 'Role is required' }}
            render={({ field, fieldState }) => (
              <>
                <Select
                  options={roleOptions}
                  value={field.value}
                  placeholder="Select a role"
                  disabled={isRolesLoading || roleOptions.length === 0}
                  onChange={value => field.onChange(value)}
                />
                {fieldState.error && (
                  <Typography variant="body4" color="error">
                    {fieldState.error.message}
                  </Typography>
                )}
              </>
            )}
          />
        </Stack>
      </Stack>
    </Modal>
  )
}

export const EditCompanyUserModal = (props: EditCompanyUserModalProps) => {
  // No hooks in this component, so this early return is safe by construction.
  if (!props.user) {
    return null
  }

  return <EditCompanyUserModalContent {...props} user={props.user} />
}
